<script setup>
import { onClickOutside } from "@vueuse/core";

const userAuth = useCookie("auth.token");
const { data: sessionData } = useAuth();
const config = useRuntimeConfig();
const { $pusher } = useNuxtApp();
const toast = useToast();

const adminEmails = [
  "info@unilinks.co",
  "admin@gmail.com",
  "superadmin@gmail.com",
];
const hasMentorProfile = computed(() => {
  const email = sessionData.value?.data.email;
  const isAgent = sessionData.value?.data.agent_status;

  return isAgent || adminEmails.includes(email);
});

const isDropdownOpen = ref(false);
const dropdownRef = ref(null);
const dialogVisible = ref(false);
const selectedApplicationId = ref();
const firstShow = ref(true);

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

const onNotificationClick = (applicationId) => {
  selectedApplicationId.value = applicationId;
  dialogVisible.value = true;
};

// -- UNREAD NOTIFICATIONS --
const { data: unreadNotifications } = useFetch(
  `${config.public.apiUrl}notifications/unread`,
  {
    headers: {
      Authorization: `Bearer ${userAuth.value}`,
      "X-Frontend-Portal": config.public.portal,
    },
    key: "unread-notifications",
    transform(notifications) {
      if (!notifications || !notifications.data) return { data: [], meta: {} };

      return {
        data: Array.isArray(notifications.data) ? notifications.data : [],
        meta: {
          current_page: notifications.current_page ?? 1,
          per_page: notifications.per_page ?? 15,
          total: notifications.total ?? 0,
          next_page_url: notifications.next_page_url ?? null,
          prev_page_url: notifications.prev_page_url ?? null,
        },
      };
    },
  },
);

// -- READ NOTIFICATIONS --
const { data: readNotifications } = useFetch(
  `${config.public.apiUrl}notifications/read`,
  {
    headers: {
      Authorization: `Bearer ${userAuth.value}`,
      "X-Frontend-Portal": config.public.portal,
    },
    key: "read-notifications",
    transform(notifications) {
      if (!notifications || !notifications.data) return { data: [], meta: {} };

      return {
        data: Array.isArray(notifications.data) ? notifications.data : [],
        meta: {
          current_page: notifications.current_page ?? 1,
          per_page: notifications.per_page ?? 15,
          total: notifications.total ?? 0,
          next_page_url: notifications.next_page_url ?? null,
          prev_page_url: notifications.prev_page_url ?? null,
        },
      };
    },
  },
);

/**
 * Loads the next page of unread or read notifications (or both)
 * when the user clicks "View Previous Notifications".
 */
const loadPreviousNotifications = async () => {
  const unreadNextPageUrl = secureUrl(
    unreadNotifications.value?.meta?.next_page_url,
  );

  const readNextPageUrl = secureUrl(
    readNotifications.value?.meta?.next_page_url,
  );

  if (unreadNextPageUrl) {
    const { data: unreadResponse } = await useFetch(unreadNextPageUrl, {
      headers: { Authorization: `Bearer ${userAuth.value}` },
    });

    // console.log("Unread Next Page Response:", unreadResponse.value);

    if (unreadResponse.value?.data?.length) {
      unreadNotifications.value.data.push(...unreadResponse.value.data);

      // Update pagination metadata for unread
      unreadNotifications.value.meta.current_page =
        unreadResponse.value.current_page ??
        unreadNotifications.value.meta.current_page + 1;
      unreadNotifications.value.meta.next_page_url =
        unreadResponse.value.next_page_url;
      unreadNotifications.value.meta.prev_page_url =
        unreadResponse.value.prev_page_url;
    }
  }

  if (readNextPageUrl) {
    // console.log("Loading NEXT PAGE for READ notifications:", readNextPageUrl);
    const { data: readResponse } = await useFetch(readNextPageUrl, {
      headers: { Authorization: `Bearer ${userAuth.value}` },
    });

    // console.log("Read Next Page Response:", readResponse.value);

    // Append new read notifications
    if (readResponse.value?.data?.length) {
      readNotifications.value.data.push(...readResponse.value.data);

      // Update pagination metadata for read
      readNotifications.value.meta.current_page =
        readResponse.value.current_page ??
        readNotifications.value.meta.current_page + 1;
      readNotifications.value.meta.next_page_url =
        readResponse.value.next_page_url;
      readNotifications.value.meta.prev_page_url =
        readResponse.value.prev_page_url;
    }
  }

  firstShow.value = false;
};

// MARK NOTIFICATION AS READ
const onMarkAsRead = async (notification) => {
  const portal = notification?.portal;
  if (portal !== null && portal !== undefined && portal !== "student") {
    redirectToOtherPortals(
      portal,
      notification.data.application_id,
      notification.read_at,
      notification.id,
    );
    return;
  }

  if (notification.data?.deleted === "delete") {
    toast.add({
      severity: "info",
      summary: "Application deleted",
      detail: "This application has been deleted.",
      life: 4000,
    });
  } else {
    onNotificationClick(notification?.data?.application_id);
  }

  if (notification.read_at) return;

  await useFetch(
    `${config.public.apiUrl}notifications/mark-as-read/${notification.id}`,
    {
      headers: {
        Authorization: `Bearer ${userAuth.value}`,
      },
      method: "POST",
      onResponse() {
        refreshNuxtData("unread-notifications");
        refreshNuxtData("read-notifications");
      },
    },
  );
};

// COMBINE BOTH NOTIFICATIONS BY CREATED_AT
const allNotifications = computed(() => {
  return [
    ...(unreadNotifications.value?.data || []),
    ...(readNotifications.value?.data || []),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

const visibleNotifications = computed(() => {
  return firstShow.value
    ? allNotifications.value.slice(0, 5)
    : allNotifications.value;
});

const unreadNotificationCount = computed(() => {
  return unreadNotifications.value?.meta?.total || 0;
});

onClickOutside(dropdownRef, closeDropdown);

const redirectToOtherPortals = (portal, applicationId, read, notiId) => {
  const mentorUrl = config.public.mentorPortalUrl;
  const adminUrl = config.public.adminPortalUrl;

  let baseUrl = "";
  const params = new URLSearchParams();

  if (read) {
    params.append("read", true);
    params.append("noti", notiId);
  } else {
    params.append("read", false);
    params.append("noti", notiId);
  }

  if (applicationId) params.append("id", applicationId);
  if (portal === "agent") {
    baseUrl = `${mentorUrl}/dashboard`;
  } else if (portal === "admin") {
    baseUrl = `${adminUrl}/applications`;
    params.append("tab", "All application");
  } else {
    console.warn(`Unknown portal: ${portal}`);
    return;
  }

  const fullUrl = `${baseUrl}?${params.toString()}`;

  return navigateTo(fullUrl, {
    external: true,
    open: { target: "_blank" },
  });
};

// HELPER
function getPortalClass(portal) {
  switch (portal) {
    case "agent":
      return "text-primary";
    case "admin":
      return "text-orange-500";
    default:
      return "text-black";
  }
}

console.log("has mentor profile: ", hasMentorProfile.value);

/** Real-time via Pusher */
onMounted(() => {
  const user_id = sessionData?.value?.data?.id;
  if (!user_id) return;

  const channel = $pusher.subscribe(`private-users.${user_id}`);

  channel.bind("gg-notification", (data) => {
    const transformedNotification = {
      id: data.id,
      type: data.type,
      notifiable_type: "App\\Models\\User",
      data: {
        state: data.state,
        message: data.message,
        application_id: data.application_id,
        profile_url: data.profile_url,
        deleted: data.deleted ?? null,
      },
      read_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      notifiable_id: user_id,
    };
    unreadNotifications.value.data.unshift(transformedNotification);
    allNotifications.value.unshift(transformedNotification);

    refreshNuxtData("unread-notifications");
    refreshNuxtData("read-notifications");
  });

  channel.bind("pusher:subscription_succeeded", () => {
    console.log("Pusher subscription succeeded");
  });

  channel.bind("pusher:subscription_error", (status) => {
    console.error("Pusher subscription error:", status);
  });
});

// DEBUGGING WATCHERS
watchEffect(() => {
  console.log("unreadNotifications changed:", unreadNotifications.value);
  // console.log("readNotifications changed:", readNotifications.value);
  // console.log("allNotifications => ", allNotifications.value);
});
</script>

<template>
  <div class="relative" ref="dropdownRef">
    <!-- Notification Bell -->
    <button
      @click="toggleDropdown"
      class="relative text-white md:bg-primary-700 border-0 md:px-2.5 py-2 rounded-lg md:hover:bg-primary-600"
    >
      <i class="pi pi-bell"></i>
      <span
        v-if="unreadNotificationCount > 0"
        class="absolute top-0 -right-2 md:-right-2 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full px-1"
      >
        {{ unreadNotificationCount }}
      </span>
    </button>

    <!-- Notification Dropdown -->
    <div
      v-if="isDropdownOpen"
      class="z-50 bg-white shadow-lg overflow-y-auto border sm:absolute sm:top-6 sm:-right-14 sm:mt-2 sm:w-[400px] sm:rounded-lg max-h-[500px] fixed top-[56px] inset-x-0 bottom-0 sm:inset-auto"
    >
      <div
        v-if="allNotifications?.length === 0"
        class="p-4 text-center text-gray-500"
      >
        No notifications
      </div>

      <div
        v-for="notification in visibleNotifications"
        :key="notification.id"
        @click="onMarkAsRead(notification)"
        class="cursor-pointer px-4 py-2 flex items-center text-black gap-x-2 hover:bg-blue-100"
        :class="{
          'bg-blue-50': !notification.read_at,
          'bg-white': notification.read_at,
        }"
      >
        <!-- Profile Image / Avatar -->
        <Avatar
          v-if="notification?.data?.profile_url"
          :image="notification.data.profile_url"
          size="large"
          shape="circle"
          class="mr-2"
        />
        <Avatar
          v-else
          icon="pi pi-user"
          size="large"
          shape="circle"
          class="mr-2"
        />

        <!-- Notification Content -->
        <div class="flex-1">
          <p class="text-sm" v-html="notification.data.message"></p>

          <div class="flex items-center gap-x-2 text-xs mt-1 text-gray-500">
            <span>{{ notiFormatDate(notification.created_at) }}</span>

            <template
              v-if="
                hasMentorProfile &&
                notification.portal &&
                notification.portal !== 'student'
              "
            >
              <span class="text-sm">·</span>
              <span
                :class="['font-medium', getPortalClass(notification.portal)]"
              >
                {{ notification.portal }}
              </span>
            </template>
          </div>
        </div>

        <!-- Unread Indicator -->
        <span
          v-if="!notification.read_at"
          class="w-2 h-2 bg-green-500 rounded-full"
        ></span>
      </div>

      <!-- See More Button -->
      <button
        v-if="allNotifications.length > 5"
        class="text-center text-sm py-4 text-blue-500 w-full hover:bg-blue-100 hover:text-blue-600"
        @click="loadPreviousNotifications"
      >
        View Previous Notifications
      </button>
    </div>
  </div>

  <Dialog
    v-model:visible="dialogVisible"
    modal
    :showHeader="false"
    :style="{ width: '97vw', height: '98vh' }"
    :pt="{ header: 'py-5 px-8 flex gap-4', footer: 'py-5 px-8 pt-0' }"
  >
    <StudentApplication
      :applicationId="selectedApplicationId"
      @closeModal="dialogVisible = false"
    />
    <template #footer>
      <ApplicationActionBtn :applicationId="selectedApplicationId" />
    </template>
  </Dialog>
</template>
